import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import WaterMeter from './pages/WaterMeter';
import ElectricityMeter from './pages/ElectricityMeter';
import AlertHistory from './pages/AlertHistory';
import Billing from './pages/Billing';
import ContactUs from './pages/ContactUs';
import Login from './pages/Login';
import Register from './pages/Register';
import { useAuth } from './context/AuthContext';
import './styles/AppLayout.css';

// Protected Route Component
const ProtectedRoute = ({ element }) => {
  const { isAuthenticated, loading } = useAuth();
  
  // If still loading, you could show a spinner
  if (loading) {
    return <div className="loading">Loading...</div>;
  }
  
  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  // If authenticated, render the requested component
  return element;
};

const App = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  // Redirect to login if not authenticated on initial load
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // Mobile detection
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        document.body.classList.add('mobile-view');
      } else {
        document.body.classList.remove('mobile-view');
        // Also ensure mobile menu is closed when resizing to desktop
        document.body.classList.remove('mobile-menu-open');
      }
    };

    // Initial check
    handleResize();
    
    // Set up listener
    window.addEventListener('resize', handleResize);
    
    // Clean up
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Log whenever the component renders
  console.log('App rendering with isAuthenticated:', isAuthenticated);

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content">
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/contact-us" element={<ContactUs />} />
          
          {/* Protected Routes */}
          <Route path="/dashboard" element={<ProtectedRoute element={<Dashboard />} />} />
          <Route path="/water-meter" element={<ProtectedRoute element={<WaterMeter />} />} />
          <Route path="/electricity-meter" element={<ProtectedRoute element={<ElectricityMeter />} />} />
          <Route path="/alert-history" element={<ProtectedRoute element={<AlertHistory />} />} />
          <Route path="/billing" element={<ProtectedRoute element={<Billing />} />} />
          <Route path="/profile" element={<ProtectedRoute element={<div>Profile Page</div>} />} />
          
          {/* Default Routes */}
          <Route path="/" element={
            isAuthenticated ? 
              <Navigate to="/dashboard" replace /> : 
              <Navigate to="/login" replace />
          } />
          <Route path="*" element={
            isAuthenticated ? 
              <Navigate to="/dashboard" replace /> : 
              <Navigate to="/login" replace />
          } />
        </Routes>
      </main>
    </div>
  );
};

export default App;
